import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import { toast } from 'react-toastify'
import {
  MDBBtn,
  MDBModal,
  MDBModalDialog,
  MDBModalContent,
  MDBModalBody,
  MDBModalFooter,
  MDBModalHeader,
  MDBModalTitle,
  MDBListGroup,
  MDBListGroupItem,
} from 'mdb-react-ui-kit'

import AdminApi from '../../../../api/admin-api/admin-api'
import ImagePreview from '../../../../components/agents/AgentHeadshotUploader/components/ImagePreview'
import AgentCorporateEmailLink from '../AgentCorporateEmailLink/AgentCorporateEmailLink.component'
import LoadingSpinner from '../../../../components/shared/LoadingSpinner.component'
import UserProfileService from '../../../../shared/services/UserProfile.service'
import AuthService from '../../../../shared/services/Auth.service'

const STAFF_ROLES = ['system-admin', 'agency-owner', 'internal-staff', 'internal-admin']

const PRIVATE_HEADER_STYLE = {
  fontSize: '12px',
  fontWeight: '500',
  color: 'rgba(120,120,120,0.6)',
  textAlign: 'center',
}

const PRIVATE_TEXT_STYLE = {
  fontSize: '14px',
  fontWeight: '500',
  color: 'rgba(120,120,120,0.8)',
  textAlign: 'center',
}

const UplinePreviewModal = ({ userId, show, onClose }) => {
  const [loading, setLoading] = useState(false)
  const [user, setUser] = useState(null)
  const [error, setError] = useState(null)
  const [isLoggingIn, setIsLoggingIn] = useState(false)
  const [viewedId, setViewedId] = useState(null)
  const [history, setHistory] = useState([])
  const isStaff = UserProfileService.isA(STAFF_ROLES, true)
  const currentId = viewedId || userId
  const previous = history.length ? history[history.length - 1] : null

  const fullName = profile => `${profile?.u_fname || ''} ${profile?.u_lname || ''}`.trim()

  const handleViewUpline = () => {
    if (!user?.u_upline_id || isLoggingIn) return
    setHistory(prev => [...prev, { id: currentId, name: fullName(user) }])
    setViewedId(user.u_upline_id)
  }

  const handleBack = () => {
    if (!previous || isLoggingIn) return
    setHistory(prev => prev.slice(0, -1))
    setViewedId(previous.id)
  }

  const handleAssumeUser = () => {
    if (!user?.id || isLoggingIn) return
    setIsLoggingIn(true)
    AuthService.assumeUser(user.id)
      .then(
        () => {
          window.location.href = '/account-profile'
        },
        err =>
          toast.error(err?.message || 'Unable to login as selected user.', {
            position: toast.POSITION.TOP_RIGHT,
          })
      )
      .catch(err =>
        toast.error(err?.message || 'Unable to login as selected user.', {
          position: toast.POSITION.TOP_RIGHT,
        })
      )
      .finally(() => setIsLoggingIn(false))
  }

  useEffect(() => {
    if (show) return
    setViewedId(null)
    setHistory([])
  }, [show])

  useEffect(() => {
    if (!show || !currentId) return

    let cancelled = false
    setLoading(true)
    setError(null)
    setUser(null)

    AdminApi.getProfileData(currentId)
      .then(result => {
        if (cancelled) return
        if (result?.data?.access && result?.data?.data) {
          setUser(result.data.data)
        } else {
          setError('You do not have access to view this profile.')
        }
      })
      .catch(() => {
        if (cancelled) return
        setError('Failed to load upline profile.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [show, currentId])

  return (
    <MDBModal open={show} onClose={onClose} tabIndex='-1'>
      <MDBModalDialog size='md'>
        <MDBModalContent>
          <MDBModalHeader>
            <MDBModalTitle>Upline Preview</MDBModalTitle>
            <MDBBtn type='button' className='btn-close' color='none' onClick={onClose}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
            {loading ? (
              <div className='text-center py-4'>
                <LoadingSpinner size='md' isActive={true} label={'Loading Upline ...'} />
              </div>
            ) : error ? (
              <div className='text-center text-danger py-3'>{error}</div>
            ) : user ? (
              <>
                <div className='text-center mb-3'>
                  <h5 className='card-title'>
                    {user.u_fname || ''} {user.u_lname || ''}
                  </h5>
                  <ImagePreview headshotUrl={user.u_picture} style={{ maxHeight: '180px', margin: '0 auto' }} />
                </div>
                <MDBListGroup>
                  {isStaff && user.id ? (
                    <MDBListGroupItem style={{ backgroundColor: 'rgba(220,220,220,0.8)' }}>
                      <div style={PRIVATE_HEADER_STYLE}>USABG STAFF ONLY - PRIVATE</div>
                      <div style={PRIVATE_TEXT_STYLE}>
                        <b>User ID:</b> {user.id}
                      </div>
                      <AgentCorporateEmailLink userId={user.id} />
                    </MDBListGroupItem>
                  ) : null}
                  {isStaff && user.u_email ? (
                    <MDBListGroupItem>
                      <b>Login:</b> {user.u_email}
                    </MDBListGroupItem>
                  ) : null}
                  {user.displayname ? (
                    <MDBListGroupItem>
                      <b>Account Type:</b> {user.displayname}
                    </MDBListGroupItem>
                  ) : null}
                  {user.d_name ? (
                    <MDBListGroupItem>
                      <b>Division:</b> {user.d_name}
                    </MDBListGroupItem>
                  ) : null}
                  {user.com_name ? (
                    <MDBListGroupItem>
                      <b>Commission Level:</b> {user.com_name}
                    </MDBListGroupItem>
                  ) : null}
                  {user.upline_fname || user.upline_lname ? (
                    <MDBListGroupItem>
                      <b>Upline Leader:</b>{' '}
                      {user.u_upline_id ? (
                        <span className='cursor-pointer text-primary' role='button' onClick={handleViewUpline}>
                          {`${user.upline_fname || ''} ${user.upline_lname || ''}`.trim()}
                        </span>
                      ) : (
                        `${user.upline_fname || ''} ${user.upline_lname || ''}`.trim()
                      )}
                    </MDBListGroupItem>
                  ) : null}
                  {isStaff && user.u_enrolled ? (
                    <MDBListGroupItem>
                      <b>Enrollment Date:</b> {moment(user.u_enrolled).format('MM/DD/YYYY')}
                    </MDBListGroupItem>
                  ) : null}
                  {isStaff && user.u_lastlogin ? (
                    <MDBListGroupItem>
                      <b>Last Login:</b> {moment(user.u_lastlogin).format('llll')}
                    </MDBListGroupItem>
                  ) : null}
                  {isStaff && user.created_at ? (
                    <MDBListGroupItem>
                      <b>Account Created:</b> {moment(user.created_at).format('llll')}
                    </MDBListGroupItem>
                  ) : null}
                </MDBListGroup>
              </>
            ) : null}
          </MDBModalBody>
          <MDBModalFooter>
            {previous ? (
              <span className='cursor-pointer text-primary small' role='button' onClick={handleBack}>
                &lsaquo; Back to {previous.name}
              </span>
            ) : null}
            {user?.id ? (
              <MDBBtn type='button' color='info' size='sm' onClick={handleAssumeUser} disabled={isLoggingIn}>
                {isLoggingIn ? 'Logging In ...' : 'View Full Profile'}
              </MDBBtn>
            ) : null}
            <MDBBtn type='button' color='danger' size='sm' onClick={onClose} disabled={isLoggingIn}>
              Close
            </MDBBtn>
          </MDBModalFooter>
        </MDBModalContent>
      </MDBModalDialog>
    </MDBModal>
  )
}

UplinePreviewModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default UplinePreviewModal
